import { Box } from '@lifeomic/chroma-react/components/Box';
import { makeStyles } from '@lifeomic/chroma-react/styles';
import React from 'react';
import { DashboardInjectedProps } from '../../util/types';

import { DotLoader } from '@lifeomic/chroma-react/components/DotLoader';
import {
  Tab,
  TabList,
  TabPanel,
  Tabs,
} from '@lifeomic/chroma-react/components/Tabs';
import { ActionItems } from './ActionItems';
import {
  ExecutiveSummaryProvider,
  useExecutiveSummaryData,
} from '../ExecutiveSummary/Context';
import { ControlBar } from '../ExecutiveSummary/ControlBar';
import { useActiveProjectId } from '../../../../hooks';
import { ClinicalActionPlanEmptyState } from './ClinicalActionPlanEmptyState';
import { ClinicalActionPlanGeneratingState } from './ClinicalActionPlanGeneratingState';
import { DuplicateActiveActionPlanBanner } from './DuplicateActiveActionPlanBanner';
import {
  hasActiveStandardCarePlan,
  hasDuplicateActiveStandardCarePlans,
} from './minimalActionPlan';
import { useCreateActionPlan } from './useCreateActionPlan';
import { useGenerateActionPlan } from './useGenerateActionPlan';
import { ZoriCoachingWorkspace } from './ZoriCoachingWorkspace';

const useStyles = makeStyles(theme => ({
  loader: {
    width: theme.pxToRem(20),
  },
  loaderDotStyle: {
    fill: theme.palette.blue.main,
  },
  actionItemsContainer: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    minHeight: 0,
    gap: theme.pxToRem(16),
    overflow: 'hidden',
    padding: theme.pxToRem(16),
    backgroundColor: theme.palette.background.default,
  },
  tabs: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    minHeight: 0,
  },
  tabPanel: {
    flex: 1,
    minHeight: 0,
    overflow: 'hidden',
  },
}));

const ClinicalActionPlan = (props: DashboardInjectedProps) => {
  const classes = useStyles();

  const {
    executiveSummary,
    isLoading,
    isRefetching,
    carePlans,
    fetchCarePlan,
    removeActivity,
    addActionItem,
    updateActionItem,
    toggleActionItemStatus,
  } = useExecutiveSummaryData();
  const hasActivePlan = hasActiveStandardCarePlan(carePlans);
  const { createActionPlan, isCreating } = useCreateActionPlan({
    patientId: props.patient.id,
    carePlans,
    fetchCarePlan,
  });
  const { generateActionPlan, isGenerating, showGenerate } =
    useGenerateActionPlan({
      patientId: props.patient.id,
      executiveSummary,
      fetchCarePlan,
      isRefetching,
      carePlans,
    });
  const hasDuplicateActives = hasDuplicateActiveStandardCarePlans(carePlans);

  if (isLoading && !executiveSummary) {
    return (
      <Box fullHeight fullWidth align="center" justify="center">
        <DotLoader
          className={classes.loader}
          dotStyle={classes.loaderDotStyle}
          size={0}
        />
      </Box>
    );
  }

  if (isGenerating) {
    return <ClinicalActionPlanGeneratingState />;
  }

  if (!hasActivePlan) {
    return (
      <ClinicalActionPlanEmptyState
        onStart={() => void createActionPlan()}
        onGenerate={() => void generateActionPlan()}
        isCreating={isCreating}
        isGenerating={isGenerating}
      />
    );
  }

  return (
    <Box direction="column" gap={0} fullHeight>
      {hasDuplicateActives ? <DuplicateActiveActionPlanBanner /> : null}
      <ControlBar
        patientId={props.patient.id}
        disableControls={true}
        enabledSummaries={['action-plan']}
        fixedImpressionType="actionItems"
        hideRegenerate={!showGenerate || hasDuplicateActives}
        onGenerateWithAi={() => void generateActionPlan()}
      />
      <Box className={classes.actionItemsContainer}>
        <ActionItems
          forcedViewMode={'expanded'}
          removeActivity={removeActivity}
          addActionItem={addActionItem}
          updateActionItem={updateActionItem}
          toggleActionItemStatus={toggleActionItemStatus}
        />
      </Box>
    </Box>
  );
};

type ActionPlanTab = 'clinical' | 'coaching';

const ActionPlanTabs = (props: DashboardInjectedProps) => {
  const classes = useStyles();
  const [selectedTab, setSelectedTab] =
    React.useState<ActionPlanTab>('clinical');
  const [hasOpenedCoaching, setHasOpenedCoaching] = React.useState(false);

  return (
    <div className={classes.tabs}>
      <Tabs selectedTabId={selectedTab} manualControl>
        <TabList aria-label="Action Plan workspace">
          <Tab stopId="clinical" onClick={() => setSelectedTab('clinical')}>
            Clinical Action Plan
          </Tab>
          <Tab
            stopId="coaching"
            onClick={() => {
              setHasOpenedCoaching(true);
              setSelectedTab('coaching');
            }}
          >
            Zori Coaching
          </Tab>
        </TabList>
        <TabPanel className={classes.tabPanel} stopId="clinical">
          <ExecutiveSummaryProvider
            patient={props.patient}
            forceLatestCarePlan={true}
          >
            <ClinicalActionPlan {...props} />
          </ExecutiveSummaryProvider>
        </TabPanel>
        <TabPanel className={classes.tabPanel} stopId="coaching">
          {hasOpenedCoaching ? (
            <ZoriCoachingWorkspace patientId={props.patient.id} />
          ) : null}
        </TabPanel>
      </Tabs>
    </div>
  );
};

const ActionPlan = (props: DashboardInjectedProps) => {
  const projectId = useActiveProjectId();
  return (
    <ActionPlanTabs key={`${projectId ?? ''}:${props.patient.id}`} {...props} />
  );
};

export default ActionPlan;
